import { isContractStatus } from '@solar/contracts';

import { paths } from '../routes/paths';

/**
 * Maps backend admin deep-link paths onto frontend routes.
 */
export function resolveAdminDeepLink(
  filterPath?: string,
  filterQuery?: Readonly<Record<string, string>>,
): string | undefined {
  if (filterPath === undefined || filterPath === '') {
    return undefined;
  }

  const status = filterQuery?.status;
  const routeMap: Record<string, string> = {
    '/admin/customers': paths.adminCustomers,
    '/admin/mandates': paths.adminMandates,
    '/admin/billing/overdue': paths.adminBilling,
    '/admin/billing/repayments': paths.adminBilling,
    '/admin/payment-attempts': paths.adminPaymentAttempts,
    '/admin/payments': paths.adminPayments,
    '/admin/notifications': paths.adminNotificationOutbox,
    '/admin/mandate-cancellation-requests': paths.adminMandateCancellations,
    '/admin/operational/applications/discount-review': paths.adminApplications,
    '/admin/reports': paths.adminReports,
    '/admin/system-health': paths.adminSystemHealth,
  };

  const frontendPath =
    filterPath === '/admin/subscriptions'
      ? status !== undefined && isContractStatus(status)
        ? paths.adminContracts
        : paths.adminApplications
      : filterPath.startsWith('/admin/subscriptions/')
        ? paths.adminApplications
        : filterPath.startsWith('/admin/mandates/')
          ? paths.adminMandates
          : filterPath.startsWith('/admin/payments/')
            ? paths.adminPayments
            : (routeMap[filterPath] ?? filterPath.replace(/^\/admin/, '/app/admin'));

  if (filterQuery === undefined || Object.keys(filterQuery).length === 0) {
    return frontendPath;
  }

  const search = new URLSearchParams({ ...filterQuery }).toString();
  return search === '' ? frontendPath : `${frontendPath}?${search}`;
}

export function formatGreeting(now: Date = new Date()): 'Good morning' | 'Good afternoon' | 'Good evening' {
  const hour = now.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export function displayNameFromEmail(email: string | undefined): string {
  if (email === undefined || email.trim() === '') {
    return 'Admin';
  }

  const local = email.split('@')[0] ?? 'Admin';
  const cleaned = local.replace(/[._-]+/g, ' ').trim();
  if (cleaned === '') {
    return 'Admin';
  }

  return cleaned
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function formatNairaAmount(value: string): string {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return `₦${value}`;
  }

  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(parsed);
}

export interface CollectionRatePresentation {
  readonly label: string;
  readonly progressPercent: number | null;
}

export function collectionRatePresentation(
  rate: string | null | undefined,
): CollectionRatePresentation {
  if (rate === null || rate === undefined || rate.trim() === '') {
    return { label: '—', progressPercent: null };
  }

  const parsed = Number(rate);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) {
    return { label: '—', progressPercent: null };
  }

  const progressPercent = parsed * 100;
  return {
    label: `${progressPercent.toFixed(0)}%`,
    progressPercent,
  };
}

export function humanizeStage(status: string): string {
  return status
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) {
    return iso;
  }

  const deltaSeconds = Math.round((then - now.getTime()) / 1000);
  const abs = Math.abs(deltaSeconds);
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

  if (abs < 60) return rtf.format(deltaSeconds, 'second');
  if (abs < 3600) return rtf.format(Math.round(deltaSeconds / 60), 'minute');
  if (abs < 86_400) return rtf.format(Math.round(deltaSeconds / 3600), 'hour');
  return rtf.format(Math.round(deltaSeconds / 86_400), 'day');
}
